import * as ToastPrimitive from '@radix-ui/react-toast';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { cn } from '../cn.js';

/**
 * Toasts report the outcome of an action the user took. They never carry information the
 * user needs to act on later — a webhook that dead-lettered belongs on a screen, not in a
 * message that disappears after five seconds.
 *
 * Errors stay until dismissed. An operator who looked away while a certificate failed to
 * stamp must not lose the only notice of it.
 */
export type ToastTone = 'success' | 'error' | 'warning' | 'info';

interface ToastItem {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  action?: { label: string; onClick: () => void };
}

interface ToastContextValue {
  toast: (t: Omit<ToastItem, 'id'>) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  /** The action succeeded, but something about it needs saying — e.g. a seal uploaded as JPEG. */
  warn: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

const ICONS: Record<ToastTone, ReactNode> = {
  success: <CheckCircle2 aria-hidden className="size-4 text-[var(--pv-ok)]" />,
  error: <XCircle aria-hidden className="size-4 text-[var(--pv-stop)]" />,
  warning: <AlertTriangle aria-hidden className="size-4 text-[var(--pv-warn)]" />,
  info: <Info aria-hidden className="size-4 text-[var(--pv-info)]" />,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const toast = useCallback((t: Omit<ToastItem, 'id'>) => {
    const id = Math.random().toString(36).slice(2);
    setItems((prev) => [...prev, { ...t, id }]);
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      toast,
      success: (title, description) => toast({ tone: 'success', title, description }),
      error: (title, description) => toast({ tone: 'error', title, description }),
      warn: (title, description) => toast({ tone: 'warning', title, description }),
      info: (title, description) => toast({ tone: 'info', title, description }),
    }),
    [toast],
  );

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {items.map((item) => (
          <ToastPrimitive.Root
            key={item.id}
            duration={item.tone === 'error' ? Infinity : item.tone === 'warning' ? 12_000 : 5500}
            onOpenChange={(open) => {
              if (!open) setItems((prev) => prev.filter((i) => i.id !== item.id));
            }}
            className={cn(
              'grid grid-cols-[auto_1fr_auto] items-start gap-3 rounded-[var(--pv-radius-lg)] border bg-[var(--pv-card)] p-3.5 shadow-[var(--pv-shadow-lg)]',
              'data-[state=open]:animate-[pv-rise_var(--pv-duration)_var(--pv-ease-out)]',
              item.tone === 'error' && 'border-[var(--pv-stop-line)]',
              item.tone === 'success' && 'border-[var(--pv-ok-line)]',
              item.tone === 'warning' && 'border-[var(--pv-warn-line)]',
              item.tone === 'info' && 'border-[var(--pv-info-line)]',
            )}
          >
            <span className="mt-0.5">{ICONS[item.tone]}</span>
            <div className="min-w-0">
              <ToastPrimitive.Title className="[font-size:var(--pv-text-sm)] font-semibold">
                {item.title}
              </ToastPrimitive.Title>
              {item.description && (
                <ToastPrimitive.Description className="mt-1 [font-size:var(--pv-text-xs)] leading-snug text-[var(--pv-muted)]">
                  {item.description}
                </ToastPrimitive.Description>
              )}
              {item.action && (
                <ToastPrimitive.Action
                  altText={item.action.label}
                  onClick={item.action.onClick}
                  className="mt-2 [font-size:var(--pv-text-xs)] font-semibold text-[var(--pv-leaf-deep)] underline underline-offset-2"
                >
                  {item.action.label}
                </ToastPrimitive.Action>
              )}
            </div>
            <ToastPrimitive.Close
              aria-label="Dismiss"
              className="rounded-[var(--pv-radius-xs)] p-1 text-[var(--pv-faint)] hover:bg-[var(--pv-paper-sunk)] hover:text-[var(--pv-ink)]"
            >
              <X aria-hidden className="size-3.5" />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed bottom-4 right-4 z-[var(--pv-z-toast)] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2 outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}
